import { Link } from "wouter";
import { isAnnualEnabled } from "@shared/pricing";
import { useSEO } from "@/hooks/use-seo";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { PricingCards } from "@/components/marketing/pricing-cards";
import { PricingEstimator } from "@/components/marketing/pricing-estimator";
import { buildPricingJsonLd } from "@/data/marketing-jsonld";
import { PRICING_SEO } from "@/data/marketing-seo";
import { pricingFaq } from "@/data/pricing-faq";
import {
  ANNUAL_MONTHS_CHARGED,
  ANNUAL_MONTHS_COVERED,
  MONEY_BACK_DAYS,
  TRIAL_DAYS,
  exampleRows,
} from "@/lib/marketing/pricing-copy";

/**
 * Public pricing page (build F5, Appendix A3): three plan cards, the
 * estimator, an example table and the pricing FAQ. Every number comes from
 * `@shared/pricing`. The annual block and annual FAQ appear only once
 * annual billing is switched on (`isAnnualEnabled()`, Nov 1, 2026).
 */
export default function PricingPage() {
  return <PricingPageView />;
}

/** The page body; `now` is a test seam for the Nov 1 annual cutover. */
export function PricingPageView({ now }: { now?: Date }) {
  const at = now ?? new Date();
  const annual = isAnnualEnabled(at);
  const faq = pricingFaq(at);

  useSEO({
    title: PRICING_SEO.title,
    description: PRICING_SEO.description,
    path: PRICING_SEO.path,
    jsonLd: buildPricingJsonLd(at),
  });

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PublicHeader />
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
        <section className="pt-28 md:pt-32 pb-14 md:pb-20 text-center">
          <div className="container mx-auto px-4 sm:px-6">
            <h1
              className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight"
              data-testid="text-pricing-heading"
            >
              PadPulse pricing
            </h1>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto" data-testid="text-pricing-subtitle">
              Plans for research, hosts and property managers. AI-drafted replies require beta access. {TRIAL_DAYS} days free, no card.
            </p>
          </div>
        </section>
      </div>

      <main className="flex-grow">
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 sm:px-6">
            <PricingCards location="pricing" />

            <ul
              className="mt-10 max-w-4xl mx-auto flex flex-col sm:flex-row sm:flex-wrap sm:justify-center gap-x-6 gap-y-2 text-sm text-gray-700 text-center"
              data-testid="list-pricing-rules"
            >
              <li>A property is one address, on or off PadSplit, with any number of rooms.</li>
              <li>No setup fees.</li>
              <li>Unlimited users.</li>
            </ul>
          </div>
        </section>

        {annual && (
          <section className="pb-4" data-testid="section-annual">
            <div className="container mx-auto px-4 sm:px-6">
              <p className="max-w-3xl mx-auto rounded-2xl border border-brand/20 bg-brand/5 p-5 text-center text-gray-800">
                Pay yearly: {ANNUAL_MONTHS_COVERED} months for the price of {ANNUAL_MONTHS_CHARGED}, with your
                price locked for the year. {MONEY_BACK_DAYS}-day money-back guarantee on annual plans.
              </p>
            </div>
          </section>
        )}

        <section className="py-12 md:py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6">
            <PricingEstimator />

            <div className="mt-10 max-w-md mx-auto" data-testid="section-example-table">
              <table className="w-full text-sm">
                <caption className="mb-3 text-left text-base font-semibold text-gray-900">
                  What hosts pay each month
                </caption>
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th scope="col" className="py-2 font-medium">Properties</th>
                    <th scope="col" className="py-2 font-medium text-right">Per month</th>
                  </tr>
                </thead>
                <tbody>
                  {exampleRows().map((row) => (
                    <tr key={row.properties} className="border-b border-gray-100" data-testid={`row-example-${row.properties}`}>
                      <td className="py-2 text-gray-900">{row.properties}</td>
                      <td className="py-2 text-right font-semibold text-gray-900">{row.monthly}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-6 text-center text-sm text-gray-600">
                Want to compare with your current tools?{" "}
                <Link href="/savings-calculator" className="font-medium text-brand hover:underline" data-testid="link-savings-calculator">
                  Try the savings calculator
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16" data-testid="section-pricing-faq">
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Pricing questions</h2>
            <dl className="space-y-6">
              {faq.map((item) => (
                <div key={item.question} className="border-b border-gray-100 pb-6">
                  <dt className="text-lg font-semibold text-gray-900">{item.question}</dt>
                  <dd className="mt-2 text-gray-700">
                    {item.answer}
                    {item.link && (
                      <>
                        {" "}
                        <Link href={item.link.href} className="text-brand hover:underline" data-testid="link-faq-terms">
                          {item.link.label}
                        </Link>
                      </>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
